import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { drawerIconButtonClass } from 'features/activity/styles/dataTableStyles';

import DiffView from 'features/prompts/components/DiffView';
import BrandSelect from 'features/playground/components/BrandSelect';
import { draftToLines, diffLines } from 'features/prompts/lib/promptDiff';
import { authorName, shortHash, timeAgo } from 'features/prompts/lib/promptMembers';

/** Read-only monospace preview of a version's full content. */
function ContentPreview({ version }) {
  const lines = useMemo(() => draftToLines(version), [version]);
  return (
    <div className="max-h-[46vh] overflow-auto border-y border-[color:var(--border-subtle)] py-2 font-mono text-[11px] leading-[1.55]">
      {lines.length === 0
        ? <span className="text-[color:var(--text-muted)]">Empty version.</span>
        : lines.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="whitespace-pre-wrap break-words text-[color:var(--text-secondary)]">{l || ' '}</div>
        ))}
    </div>
  );
}

/**
 * Version detail popup, opened from the activity timeline. Versions are
 * referenced by their hash (never a v-number). Two actions:
 *   - Load in editor  → loads this version into the editor draft.
 *   - Diff            → a dropdown appears right here; pick any other version
 *                       and the diff renders inline in the same view.
 */
export default function VersionPopup({
  version, versions, members, canEdit, teamLabels, onClose, onLoad, onToggleLabel, onCreateLabel,
}) {
  const [mode, setMode] = useState('view'); // 'view' | 'diff'
  const [otherKey, setOtherKey] = useState(''); // version_hash of the compare target

  // "+ Add label" — a small picker (pick existing, or create new), never an
  // immediate apply. Picking or creating one always drops into a confirm step
  // below, so nothing is assigned without the user explicitly saying so.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [newLabelText, setNewLabelText] = useState('');
  const [pendingLabel, setPendingLabel] = useState(null);
  const pickerRef = useRef(null);

  const pendingHolder = pendingLabel
    ? versions.find((v) => v.version_hash !== version.version_hash && (v.labels || []).includes(pendingLabel))
    : null;
  const availableLabels = (teamLabels || []).filter((l) => !(version.labels || []).includes(l));

  // If the caller swaps which version this popup shows, drop any pending
  // confirmation/diff/picker state — it referred to the previous version.
  useEffect(() => {
    setPendingLabel(null);
    setMode('view');
    setOtherKey('');
    setPickerOpen(false);
    setCreatingLabel(false);
    setNewLabelText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version.version_hash]);

  // Close the picker on an outside click.
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDoc = (e) => { if (!pickerRef.current?.contains(e.target)) setPickerOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  // Picking a label (existing or freshly created) never applies immediately —
  // it always surfaces the confirm step below, whether it's a plain add or a
  // move off another version.
  const pickLabel = (l) => {
    setPendingLabel(l);
    setPickerOpen(false);
    setCreatingLabel(false);
    setNewLabelText('');
  };

  const confirmNewLabel = async () => {
    const raw = newLabelText.trim();
    if (!raw) return;
    const created = await onCreateLabel(raw);
    if (created) pickLabel(created);
  };

  const other = versions.find((v) => v.version_hash === otherKey) || null;

  // Diff oldest → newest so additions read as "+". Ordering still uses the
  // internal version_no; only the *labels* are hashes.
  const diff = useMemo(() => {
    if (!other) return { rows: [], older: null, newer: null };
    const [older, newer] = version.version_no < other.version_no
      ? [version, other] : [other, version];
    return { rows: diffLines(draftToLines(older), draftToLines(newer)), older, newer };
  }, [version, other]);

  const [copied, setCopied] = useState(false);
  const copyHash = async () => {
    if (!version.version_hash) return;
    try {
      await navigator.clipboard.writeText(version.version_hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) { /* clipboard blocked */ }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="flex max-h-[88vh] w-[640px] max-w-[94vw] flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {/* header: the hash is the identity, so it leads */}
        <div className="shrink-0 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyHash}
              title={`Copy full hash\n${version.version_hash || ''}`}
              className="inline-flex items-center gap-1.5 font-mono text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:text-[color:var(--trodo-primary)]"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />} {shortHash(version.version_hash) || 'unversioned'}
            </button>
            <div className="flex-1" />
            <button type="button" onClick={onClose} aria-label="Close" title="Close" className={drawerIconButtonClass}><X size={15} /></button>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-[color:var(--text-muted)]">
            <span className="font-medium text-[color:var(--text-secondary)]">{authorName(version, members)}</span>
            <span className="text-[color:var(--text-disabled)]">·</span>
            <span>{timeAgo(version.created_at)}</span>
            {version.commit_message ? (
              <>
                <span className="text-[color:var(--text-disabled)]">·</span>
                <span className="min-w-0 truncate text-[color:var(--text-disabled)]">{version.commit_message}</span>
              </>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3">

        {/* label management (editors only) */}
        {canEdit && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {(version.labels || []).map((l) => (
                <span
                  key={l}
                  className="inline-flex h-6 items-center gap-1 rounded-md border border-[color:var(--border-default)] pl-2 pr-1 text-[11px] text-[color:var(--text-primary)]"
                >
                  {l}
                  <button
                    type="button"
                    onClick={() => onToggleLabel(version, l)}
                    title={`Remove ${l}`}
                    aria-label={`Remove ${l}`}
                    className="rounded p-0.5 text-[color:var(--text-muted)] hover:bg-surface-hover"
                  >
                    <X size={9} />
                  </button>
                </span>
              ))}

              <div ref={pickerRef} className="relative">
                <button
                  type="button"
                  onClick={() => setPickerOpen((o) => !o)}
                  className="inline-flex h-6 items-center rounded-md border border-[color:var(--border-default)] px-2 text-[11px] text-[color:var(--text-primary)] hover:bg-surface-hover"
                >
                  Add label
                </button>

                {pickerOpen && (
                  <div className="absolute left-0 top-[calc(100%+4px)] z-30 w-52 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-lg">
                    {!creatingLabel ? (
                      <>
                        {availableLabels.length === 0 && (
                          <div className="px-3 py-2 text-[11px] text-[color:var(--text-disabled)]">No other labels yet</div>
                        )}
                        {availableLabels.map((l) => (
                          <button
                            key={l}
                            type="button"
                            onClick={() => pickLabel(l)}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-hover)]"
                          >
                            {l}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setCreatingLabel(true)}
                          className="flex w-full items-center gap-2 border-t border-[color:var(--border-subtle)] px-3 py-1.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)]"
                        >
                          New label…
                        </button>
                      </>
                    ) : (
                      <div className="px-2 py-1.5">
                        <input
                          autoFocus
                          value={newLabelText}
                          onChange={(e) => setNewLabelText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') confirmNewLabel(); if (e.key === 'Escape') setCreatingLabel(false); }}
                          placeholder="e.g. canary"
                          className="w-full rounded-md border border-[color:var(--border-default)] bg-surface px-2 py-1 text-[12px] text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--border-strong)]"
                        />
                        <div className="mt-1.5 flex justify-end gap-1.5">
                          <button type="button" onClick={() => setCreatingLabel(false)} className="rounded-md px-2 py-1 text-[11px] text-[color:var(--text-muted)] hover:bg-surface-hover">Cancel</button>
                          <button type="button" onClick={confirmNewLabel} className="h-8 rounded-md bg-[color:var(--trodo-primary)] px-2.5 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90">Add</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Picking a label never applies immediately: confirm what it does:
                a plain add, or a move that pulls it off another version. */}
            {pendingLabel && (
              <div className="mt-2 border-t border-[color:var(--border-subtle)] pt-2 text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
                <div className="mb-2">
                  {pendingHolder ? (
                    <>
                      Move <span className="font-medium">{pendingLabel}</span> from{' '}
                      <span className="font-mono">{shortHash(pendingHolder.version_hash)}</span> to{' '}
                      <span className="font-mono">{shortHash(version.version_hash)}</span>?{' '}
                      <span className="font-mono">{shortHash(pendingHolder.version_hash)}</span> will no longer be labelled <span className="font-medium">{pendingLabel}</span>.
                    </>
                  ) : (
                    <>
                      Add <span className="font-medium">{pendingLabel}</span> to{' '}
                      <span className="font-mono">{shortHash(version.version_hash)}</span>?
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingLabel(null)}
                    className="h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => { onToggleLabel(version, pendingLabel); setPendingLabel(null); }}
                    className="inline-flex h-8 items-center rounded-md bg-[color:var(--trodo-primary)] px-2.5 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90"
                  >
                    {pendingHolder ? 'Confirm move' : 'Confirm'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* diff picker: a dropdown right here, diff renders inline below it */}
        {mode === 'diff' && (
          <div className="mb-2 flex items-center gap-2 text-[11.5px]">
            <span className="shrink-0 text-[color:var(--text-muted)]">Compare with</span>
            <div className="min-w-0 flex-1">
              <BrandSelect
                value={otherKey}
                placeholder="Choose a version…"
                minWidth={0}
                options={versions.filter((v) => v.version_hash !== version.version_hash).map((v) => ({
                  value: v.version_hash,
                  label: (
                    <span>
                      <span className="font-mono">{shortHash(v.version_hash)}</span>
                      {' · '}{authorName(v, members)}{' · '}{timeAgo(v.created_at)}
                    </span>
                  ),
                }))}
                onChange={(v) => setOtherKey(v)}
              />
            </div>
            {other && diff.older.content_hash && diff.older.content_hash === diff.newer.content_hash && (
              <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">Identical</span>
            )}
          </div>
        )}

        {/* body */}
        {mode === 'view' && <ContentPreview version={version} />}
        {mode === 'diff' && !other && (
          <div className="py-6 text-center text-[12px] text-[color:var(--text-muted)]">
            Pick a version above to see the diff.
          </div>
        )}
        {mode === 'diff' && other && (
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10.5px] text-[color:var(--text-disabled)]">
              <span>{shortHash(diff.older.version_hash)}</span>
              <span>→</span>
              <span>{shortHash(diff.newer.version_hash)}</span>
            </div>
            <DiffView diff={diff.rows} emptyLabel="These two versions are identical." />
          </div>
        )}

        </div>

        {/* footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[color:var(--border-subtle)] px-4 py-3">
          {mode === 'view' ? (
            <>
              <button
                type="button"
                onClick={() => { setMode('diff'); setOtherKey(''); }}
                className="inline-flex h-8 items-center rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover"
              >
                Diff
              </button>
              <button
                type="button"
                onClick={() => { onLoad(version); onClose(); }}
                title={canEdit ? 'Load this version into the editor' : 'Loads into the editor (view-only: inspect, but you cannot save)'}
                className="inline-flex h-8 items-center rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] hover:opacity-90"
              >
                Load in editor
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => { setMode('view'); setOtherKey(''); }}
              className="inline-flex h-8 items-center rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover"
            >
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
